import cn from 'classnames'
import type { IState } from 'legacy/lut/store'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import {
  AttentionSeverityPlate,
  type IRaisedAttentionStatus,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { IAttentionPartnerOverview } from '../../../../lib'
import { selectCockpitPartnerAttentionStatuses } from '../../../../model/selectors'
import { CockpitPartnerScope } from '../../../components/cockpitPartnerScope'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import { mergeSameTitledStatuses } from '../../lib'
import { renderAttentionStatusSection } from '../../renderAttentionStatusSection'
import { AttentionPartnerLink } from '../attentionPartnerLink'
import { AttentionStatusPlaceholder } from '../attentionStatusPlaceholder'

import styles from './AttentionStatusesFullscreen.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.fullscreen

/** A status row's identity: `Overall` lists the same status once per offering that raised it. */
const toStatusId = ({ bitValue, offerings }: IRaisedAttentionStatus) =>
  `${bitValue}|${offerings[0] ?? ''}`

interface IAttentionFullscreenPartnerWorkspaceProps {
  overview: IAttentionPartnerOverview
  /** The status the view is filtered to, opened first where the partner holds it. */
  preferredBit: number | null
  /** The offering the preferred status is opened for, where `Overall` lists it per offering. */
  preferredOffering?: string | null
  onClose: () => void
}

/**
 * One partner of a PBM's list, worked on in place: every status it holds open as a switch on top,
 * and under it the picked status' own section with the actions the partner's own cockpit offers.
 */
export const AttentionFullscreenPartnerWorkspace = ({
  overview,
  preferredBit,
  preferredOffering = null,
  onClose,
}: IAttentionFullscreenPartnerWorkspaceProps) => {
  const t = useSafetyTranslation()
  const text = useAttentionStatusesText()
  const { partner } = overview
  const [pickedId, setPickedId] = useState<string | null>(null)

  const raised = useSelector((state: IState) =>
    selectCockpitPartnerAttentionStatuses(state, partner.partnerId)
  )

  /* Worst first, as the list the partner was picked from orders its partners. */
  const statuses = useMemo(
    () =>
      mergeSameTitledStatuses(raised, (status) => text.statusTitleParts(status, 1).text).sort(
        (left, right) => right.severity - left.severity || left.sortOrder - right.sortOrder
      ),
    [raised, text]
  )

  const selected =
    statuses.find((status) => toStatusId(status) === pickedId) ??
    statuses.find(
      ({ bitValue, offerings }) =>
        bitValue === preferredBit && !!preferredOffering && offerings.includes(preferredOffering)
    ) ??
    statuses.find(({ bitValue }) => bitValue === preferredBit) ??
    statuses[0]

  const scopeStatus = useMemo(
    () =>
      selected ? { bitValue: selected.bitValue, offering: selected.offerings[0] ?? null } : null,
    [selected]
  )

  const detailStatus = useMemo(
    () => selected && { ...selected, title: text.statusTitleParts(selected, 1).text },
    [selected, text]
  )

  const handlePick = useCallback((status: IRaisedAttentionStatus) => {
    setPickedId(toStatusId(status))
  }, [])

  return (
    <section className={styles.workspace} aria-label={partner.partnerName}>
      <div className={styles.workspaceHead}>
        <span className={styles.workspaceTitle}>
          <span className={styles.scopeName}>{partner.partnerName}</span>
          <span className={styles.scopeSub}>{partner.country}</span>
        </span>
        <AttentionPartnerLink
          variant="text"
          partnerId={partner.partnerId}
          partnerName={partner.partnerName}
          className={styles.planButton}
        />
        <button
          type="button"
          className={styles.partnerPanelClose}
          aria-label={t(KEYS.close)}
          onClick={onClose}
        >
          <i className="kbase kbase-close" aria-hidden="true" />
        </button>
      </div>

      {statuses.length === 0 || !detailStatus || !scopeStatus ? (
        <p className={styles.empty}>
          <Translation span text={KEYS.allClear} />
        </p>
      ) : (
        <>
          <div className={styles.workspaceStatuses} role="tablist">
            {statuses.map((status) => {
              const isSelected = status === selected

              return (
                <button
                  key={toStatusId(status)}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  className={cn(styles.workspaceStatus, {
                    [styles.workspaceStatusSelected]: isSelected,
                  })}
                  onClick={() => handlePick(status)}
                >
                  {/* The plate's size is its own prop — its inline variable outranks a class — so it is
                      set to the height the chip leaves it. */}
                  <AttentionSeverityPlate
                    severity={status.severity}
                    size={22}
                    pulse={false}
                    className={styles.workspaceStatusPlate}
                  >
                    !
                  </AttentionSeverityPlate>
                  <span className={styles.tabLabel}>{text.statusTitleParts(status, 1).text}</span>
                </button>
              )
            })}
          </div>

          <div className={styles.workspaceDetail}>
            <CockpitPartnerScope
              partnerId={partner.partnerId}
              partnerName={partner.partnerName}
              status={scopeStatus}
            >
              <AttentionStatusPlaceholder
                key={toStatusId(detailStatus)}
                status={detailStatus}
                partnerId={partner.partnerId}
                section={renderAttentionStatusSection(detailStatus.bitValue)}
                onBack={onClose}
              />
            </CockpitPartnerScope>
          </div>
        </>
      )}
    </section>
  )
}

AttentionFullscreenPartnerWorkspace.displayName = 'AttentionFullscreenPartnerWorkspace'
